var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import labelStyles from '@cof/fs-field-label-core/lib/field-label.lit.min.js';
import { FormControlMixin } from '@cof/fs-form-control';
import formFieldStyles from '@cof/fs-form-field-core/lib/form-field.lit.min.js';
import helperTextStyles from '@cof/fs-helper-text-core/lib/helper-text.lit.min.js';
import styles from '@cof/fs-text-field-core/lib/text-field.lit.min.js';
import { css, html, LitElement, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
const eventForwarder = (target) => (event) => {
    event.stopPropagation();
    const e = new Event(event.type, event);
    target.dispatchEvent(e);
};
/**
 * The web component implementation of the Financial Services text field component
 *
 * ## Installation
 *
 * ```bash
 * npm i @cof/fs-text-field-wc
 * ```
 *
 * ## Usage
 *
 * To import the side-effectful registration of `<fs-text-field>` call
 *
 * ```javascript
 * import '@cof/fs-text-field-wc/fs-text-field.js';
 * ```
 *
 * To import the `FsTextField` class as either a base class or to self-register the custom element use
 *
 * ```javascript
 * import { FsTextField } from '@cof/fs-text-field-wc';
 * ```
 *
 * ## Example
 *
 * ```html
 * <form>
 *  <fs-text-field name="text-field">
 *    <fs-icon slot="leading-icon" icon="fs-24--placeholder"></fs-icon>
 *    Text field label
 *    <fs-icon slot="trailing-icon" icon="fs-24--placeholder" interactive description="Perform an action"></fs-icon>
 *  </fs-text-field>
 * </form>
 * ```
 *
 * @element fs-text-field
 * @fires input - Fired when the control's value changes on keyboard interaction
 * @fires change - Notifies when the control's value is comitted (typically on blur)
 * @slot - The text field's label text
 * @slot leading-icon - The text field's leading icon which is typically non-interactive per the rules in the component's design spec
 * @slot trailing-icon - The text field's trailing icon which is typically an interactive icon per the component's design spec
 * @element fs-text-field
 */
export class FsTextField extends FormControlMixin(LitElement) {
    constructor() {
        super(...arguments);
        /** Placeholder */
        this._value = '';
        /** The autocomplete settings for the input */
        this.autocomplete = 'off';
        /** If true, the browser should automatically focus on the input */
        this.autofocus = false;
        /** If true, the input will use compact styles */
        this.compact = false;
        /** If true, the input will be disabled */
        this.disabled = false;
        /**
         * If true, the label will be visually hidden but remain for a11y purposes
         * @attribute hide-label
         */
        this.hideLabel = false;
        /** Proxy for the input's inputmode attribute */
        this.inputmode = null;
        /**
         * If set, helper text will be displayed below the input
         * @attribute helper-text
         */
        this.helperText = '';
        /** A proxy for the input's max attribute */
        this.max = undefined;
        /**
         * A proxy for the input's maxlength attribute
         * @attribute maxlength
         */
        this.maxLength = null;
        /** A proxy for the input's min attribute */
        this.min = undefined;
        /**
         * A proxy for the input's minlength attribute
         * @attribute maxlength
         */
        this.minLength = null;
        /** The input's name */
        this.name = null;
        /**
         * If true, the input will not use input validators
         * @attribute no-validate
         */
        this.noValidate = false;
        /** An HTML-safe RegExp pattern for validation purposes */
        this.pattern = undefined;
        /** The placeholder string on the input element */
        this.placeholder = '';
        /**
         * A proxy for the input's readonly attribute
         * @attribute readonly
         */
        this.readOnly = false;
        /** If true, the input is required if used in the context of a form */
        this.required = false;
        /** The step numerical values can be incremented or decremented by. Only useful for number type inputs. */
        this.step = undefined;
        /** The input's type */
        this.type = 'text';
        this.leadingIconInteractive = false;
        this.trailingIconInteractive = false;
        this.forwardEvent = eventForwarder(this);
    }
    static get styles() {
        return [
            styles,
            labelStyles,
            formFieldStyles,
            helperTextStyles,
            css `
      :host { display: block; }
    `
        ];
    }
    /** The input's value */
    get value() {
        return this._value;
    }
    set value(value) {
        this._value = value;
        this.reconcileValue(value);
        this._internals.setFormValue(value);
        this.reconcileValidity();
    }
    firstUpdated(changed) {
        super.firstUpdated(changed);
        this.linkControl(this.input);
        if (this.leading.querySelector('[icon]')) {
            this.leadingIconInteractive = this.leading.querySelector('[icon]').hasAttribute('interactive');
        }
    }
    render() {
        return html `<div class="fs-form-field ${this.compact ? 'fs-form-field--compact' : ''}">
      <label 
        class="fs-field-label ${this.compact ? 'fs-field-label--compact' : ''} ${this.disabled ? 'fs-field-label--disabled' : ''} ${this.hideLabel ? 'fs-field-label--hidden' : ''}" for="input">
      <slot></slot> 
      ${this.required ? '' : html `<span class="fs-field-label__optional">(optional)</span>`}
      </label>
      
      <div 
      class="fs-text-field ${this.compact ? 'fs-text-field--compact' : ''} ${this.disabled ? 'fs-text-field--disabled' : ''} ${this.leadingIconInteractive ? 'fs-text-field--interactive-leading' : ''} ${this.trailingIconInteractive ? 'fs-text-field--interactive-trailing' : ''} ${this.showError ? 'fs-text-field--error' : ''} ${this.readOnly ? 'fs-text-field--readonly' : ''}"
      @interactive-toggle="${this._interactiveIconUpdate}">
        <slot name="leading-icon"></slot>
        <input
          id="input"
          autocomplete="${this.autocomplete}"
          aria-describedby="helper-text"
					?autofocus="${this.autofocus}"
					?disabled="${this.disabled}"
          inputmode="${this.inputmode}"
					max="${ifDefined(this.max)}"
					maxlength="${this.maxLength}"
					min="${ifDefined(this.min)}"
					minlength="${this.minLength}"
          name="${this.name}"
          pattern="${ifDefined(this.pattern)}"
					placeholder="${this.placeholder}"
					?readonly="${this.readOnly}"
					?required="${this.required}"
          step="${ifDefined(this.step)}"
          type="${this.type}"
          .value="${live(this.value)}"
					@change="${this._onInputUpdate}"
          @input="${this._onInputUpdate}"
          part="fs-input__input"
        >
        <slot name="trailing-icon"></slot>
      </div>
      ${this.noValidate
            ? nothing
            : html `
            <span
              class="fs-helper-text ${this.showError ? 'fs-helper-text--error' : ''}" 
              id="helper-text"
              aria-atomic="true"
              role="alert">
              ${!this.showError ? this.helperText : this.validationMessage}
            </span>`}
      </div>`;
    }
    formResetCallback() {
        this.value = '';
        this._internals.setFormValue('');
    }
    _interactiveIconUpdate(event) {
        event.stopPropagation();
        const icon = event.target;
        const location = icon.assignedSlot.name;
        if (location === 'leading-icon') {
            this.leadingIconInteractive = event.detail;
        }
        else {
            this.trailingIconInteractive = event.detail;
        }
    }
    /** Transform the value of the input on input and change */
    _onInputUpdate(event) {
        this.value = event.target.value;
        this.checkValidity();
        this.forwardEvent(event);
    }
}
/** The element's canonical name for use with safe define */
FsTextField.canonicalName = 'text-field';
FsTextField.shadowRootOptions = {
    delegatesFocus: true,
    mode: 'open'
};
__decorate([
    property({ type: String, reflect: true })
], FsTextField.prototype, "autocomplete", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsTextField.prototype, "autofocus", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsTextField.prototype, "compact", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsTextField.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsTextField.prototype, "hideLabel", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsTextField.prototype, "inputmode", void 0);
__decorate([
    property({ type: String, attribute: 'helper-text' })
], FsTextField.prototype, "helperText", void 0);
__decorate([
    property({ type: Number, reflect: true })
], FsTextField.prototype, "max", void 0);
__decorate([
    property({ type: Number, reflect: true })
], FsTextField.prototype, "maxLength", void 0);
__decorate([
    property({ type: Number, reflect: true })
], FsTextField.prototype, "min", void 0);
__decorate([
    property({ type: Number, reflect: true })
], FsTextField.prototype, "minLength", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsTextField.prototype, "name", void 0);
__decorate([
    property({ type: Boolean, attribute: 'no-validate' })
], FsTextField.prototype, "noValidate", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsTextField.prototype, "pattern", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsTextField.prototype, "placeholder", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsTextField.prototype, "readOnly", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsTextField.prototype, "required", void 0);
__decorate([
    property({ type: Number })
], FsTextField.prototype, "step", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsTextField.prototype, "type", void 0);
__decorate([
    query('input')
], FsTextField.prototype, "input", void 0);
__decorate([
    state()
], FsTextField.prototype, "leadingIconInteractive", void 0);
__decorate([
    state()
], FsTextField.prototype, "trailingIconInteractive", void 0);
__decorate([
    query('slot[name="leading-icon"]')
], FsTextField.prototype, "leading", void 0);
__decorate([
    query('slot[name="trailing-icon"]')
], FsTextField.prototype, "trailing", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsTextField.prototype, "value", null);
//# sourceMappingURL=TextField.js.map